import { omit } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  BLOCK_STATUS,
  CONVERTING_STATUS,
  PROJECT_PRODUCT,
  MAX_CHARACTER_PER_BLOCK,
  MAX_CHARACTER_PER_BLOCK_SRT,
  TRANSLATION_STATUS,
  AUTO_SAVE_STATUS,
  DEFAULT_TRACK_VOLUMES,
} from '@src/constants/project';
import {
  ASPECT_RATIO_VALUES,
  CAPTION_STYLE_CUSTOM,
  CAPTION_STYLE_NONE,
  DEFAULT_VIDEO_BACKGROUND_COLOR,
} from '@src/constants/dubbing';
import { CAPTION_STYLE_DEFAULTS } from '@vbee-holding/aivoice-shared-lib';
import {
  getBlockChanged,
  getBlockStatus,
  resolveBlockVoice,
  getDubbingBlockChanged,
  isVideoSettingsChanged,
  invalidateBlockAudio,
  appendExportPage,
  mergeProgressIntoState,
  isOwnCaptionStyle,
  withCustomCaptionStyle,
} from '@src/services/project';
import {
  pickBlockContent,
  musicClipsToMusicBlocks,
  trackVolumesToStored,
} from '@src/services/autoSave';
import { applyForward, applyHistorySnapshots } from '@src/services/history';
import { HISTORY_SCOPE } from '@src/constants/history';
import { EXPORT_TYPE } from '@src/constants/presentation';
import { actionTypes } from './actions';
import { actionTypes as historyActionTypes } from '../history/actions';

// A new synthesis regenerates a block's audio, which makes any previously
// exported file (pptx/video) stale (the Backend drops its fileUrl on block
// change). Mirror that here so the download button re-exports instead of
// serving the old file.
const invalidateExports = (exports) =>
  exports ? omit(exports, Object.values(EXPORT_TYPE)) : exports;

// BGM — the music clips and the per-track volumes — is mixed into the rendered
// VIDEO and the joined AUDIO, so changing it makes those cached exports stale
// (the Backend drops them too). Separate audio is per-block with no mix, so it
// stays. Mirror invalidateExports for the download button to re-export.
const invalidateMixExports = (exports) =>
  exports
    ? omit(exports, [EXPORT_TYPE.VIDEO, EXPORT_TYPE.JOIN_AUDIO])
    : exports;

// API trả config thưa, phủ default cho nhóm mà màn cấu hình đang đọc.
const mergeCaptionConfig = (config) => ({
  ...config,
  position: { ...CAPTION_STYLE_DEFAULTS.position, ...config?.position },
  text: { ...CAPTION_STYLE_DEFAULTS.text, ...config?.text },
  outline: { ...CAPTION_STYLE_DEFAULTS.outline, ...config?.outline },
  layout: { ...CAPTION_STYLE_DEFAULTS.layout, ...config?.layout },
  background: { ...CAPTION_STYLE_DEFAULTS.background, ...config?.background },
  shadow: { ...CAPTION_STYLE_DEFAULTS.shadow, ...config?.shadow },
  highlight: { ...CAPTION_STYLE_DEFAULTS.highlight, ...config?.highlight },
});

const createDefaultVideoSettings = () => ({
  aspectRatio: ASPECT_RATIO_VALUES.ORIGINAL,
  backgroundColor: DEFAULT_VIDEO_BACKGROUND_COLOR,
  captionStyle: { styleId: CAPTION_STYLE_NONE },
});

export const initialState = {
  projectId: '',
  multiBlock: {
    blocks: {},
    blockIds: [],
    originalBlockIds: [],
    originalBlocks: {},
    blockCredits: {},
  },
  title: '',
  createdAt: '',
  product: '',
  isPlaying: false,
  isPlayingBlock: false,
  currentPlayingBlockId: '',
  currentBlockId: '',
  audioLink: '',
  voiceDefault: {},
  listVoice: [],
  speakers: [],
  isDraft: false,
  isMultiBlockMode: false,
  duration: 0,
  currentTime: 0,
  isDownloading: false,
  exports: null,
  // One list for the whole project: the poll writes statuses here and the
  // history tab reads them, so neither side keeps its own copy.
  exportRecords: { items: [], nextCursor: null },
  downloadingExportIds: [],
  isProjectSynthesizing: false,
  isGeneratingNarration: false,
  generatingBlockIds: [],
  originalTitle: '',
  audioType: 'mp3',
  openDialogChangeFormat: false,
  originalAudioType: 'mp3',
  currentFilteringStatus: BLOCK_STATUS.ALL,
  startFilterBlock: false,
  selectedBlockIds: [],
  currentFilteringVoiceCode: '',
  currentFilteringSpeakerId: null,
  audioLinkSrt: '',
  audioExpiredAt: '',
  // Background-music clips on the timeline's music track, in timeline order:
  // { id, name, link, volume, startSec, endSec }. `volume` is the picker's own
  // 0..100 per-clip level (the render scene uses the same scale) — NOT the
  // 0..1 the audioPlayer slice keeps for a whole track. Playback multiplies the
  // two, so a clip's level is `volume / 100 * trackVolume`.
  // Editor-only for now: the backend has no field for these, so they are lost
  // on reload. Shaped as a list to match the render scene's `musicBlocks`.
  musicClips: [],
  // Mixer level per track, 0..1. Project state (auto-save persists it, the export
  // mixes it in) — keyed by the roles the Backend `volumes` field uses. A music
  // clip's own 0..100 `volume` multiplies on top of `music`.
  volumes: { ...DEFAULT_TRACK_VOLUMES },
  status: '',
  dubbingRequestId: '',
  isProjectConvertingDraft: false,
  isPreProcessingSynthesis: false,
  translation: null,
  // Pauses and dictionary the latest conversion ran with; captions are timed with them.
  synthesisParams: null,
  isTranslating: false,
  isTranslationPendingSynthesis: false,
  showOriginalText: false,
  isLoadingProjectDetail: true,
  source: null,
  isBuffering: false,
  videoSettings: createDefaultVideoSettings(),
  originalVideoSettings: createDefaultVideoSettings(),
  autoSaveStatus: AUTO_SAVE_STATUS.IDLE,
  autoSaveEnabled: false,
  autoSaveVideoConfig: {
    aspectRatioEnabled: false,
    backgroundColorEnabled: false,
  },
  lastSaved: {
    blockIds: [],
    blocks: {},
    title: '',
    audioType: 'mp3',
    videoSettings: null,
    musicBlocks: [],
    volumes: null,
  },
};

export default function projectReducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.SET_PROJECT_ID:
      return {
        ...state,
        projectId: action.payload,
      };

    case actionTypes.SET_MULTI_BLOCK: {
      const multiBlock = action.payload;
      const maxCharLimit =
        state.product === PROJECT_PRODUCT.DUBBING
          ? MAX_CHARACTER_PER_BLOCK_SRT
          : MAX_CHARACTER_PER_BLOCK;
      const blocksWithStatus = Object.values(multiBlock?.blocks || {}).reduce(
        (acc, block) => {
          const blockStatus = getBlockStatus({
            block,
            originalBlock: multiBlock?.originalBlocks?.[block.id],
            audioType: state.audioType,
            maxCharLimit,
            voice: resolveBlockVoice({
              block,
              speakers: state.speakers,
              voiceDefault: state.voiceDefault,
            }),
            isTranslationSuccess:
              state.translation?.status === TRANSLATION_STATUS.SUCCESS,
          });
          acc[block.id] = { ...block, blockStatus };
          return acc;
        },
        {},
      );
      return {
        ...state,
        multiBlock: {
          ...multiBlock,
          blocks: blocksWithStatus,
        },
        // A fresh project load is a clean baseline — clear any leftover
        // SAVING/ERROR status from a previous visit so re-entering resets it.
        autoSaveStatus: AUTO_SAVE_STATUS.IDLE,
        lastSaved: {
          blockIds: multiBlock.blockIds || [],
          blocks: (multiBlock.blockIds || []).reduce((acc, id) => {
            acc[id] = pickBlockContent(multiBlock.blocks?.[id], state.product);
            return acc;
          }, {}),
          title: state.title,
          audioType: state.audioType,
          videoSettings: state.videoSettings,
          musicBlocks: musicClipsToMusicBlocks(state.musicClips),
          volumes: trackVolumesToStored(state.volumes),
        },
      };
    }

    case actionTypes.SET_TITLE:
      return {
        ...state,
        title: action.payload,
      };

    case actionTypes.UPDATE_TITLE:
      return {
        ...state,
        title: action.payload,
        isDraft: true,
      };

    case actionTypes.SET_LIST_VOICE:
      return {
        ...state,
        listVoice: action.payload,
      };

    case actionTypes.SET_CREATED_AT:
      return {
        ...state,
        createdAt: action.payload,
      };

    case actionTypes.SET_PRODUCT:
      return {
        ...state,
        product: action.payload,
      };

    case actionTypes.SET_IS_PLAYING:
      return {
        ...state,
        isPlaying: action.payload,
      };

    case actionTypes.SET_IS_PLAYING_BLOCK:
      return {
        ...state,
        isPlayingBlock: action.payload,
      };

    case actionTypes.SET_CURRENT_BLOCK_ID:
      return {
        ...state,
        currentBlockId: action.payload,
      };

    case actionTypes.SET_CURRENT_PLAYING_BLOCK_ID:
      return {
        ...state,
        currentPlayingBlockId: action.payload,
      };

    case actionTypes.SET_VOICE_DEFAULT:
      return {
        ...state,
        voiceDefault: action.payload,
      };

    case actionTypes.SET_AUDIO_LINK:
      return {
        ...state,
        audioLink: action.payload,
      };

    case actionTypes.SET_IS_DRAFT:
      return {
        ...state,
        isDraft: action.payload,
      };

    case actionTypes.SET_MUSIC_CLIPS: {
      return { ...state, musicClips: action.payload };
    }

    case actionTypes.ADD_MUSIC_CLIP: {
      // Kept sorted by start time so appending and inserting into a gap both
      // keep array order == time order (neighbour bounds / gaps rely on it).
      return {
        ...state,
        exports: invalidateMixExports(state.exports),
        musicClips: [...state.musicClips, action.payload].sort(
          (a, b) => a.startSec - b.startSec,
        ),
      };
    }

    case actionTypes.DUPLICATE_MUSIC_CLIP: {
      const { afterId, clip } = action.payload;
      const index = state.musicClips.findIndex((c) => c.id === afterId);
      if (index === -1) return state;
      // The copy sits right after the original; push the clips beyond it right
      // by its length so it drops into a real gap instead of overlapping them.
      const shift = clip.endSec - clip.startSec;
      const next = state.musicClips.map((c, i) =>
        i > index
          ? { ...c, startSec: c.startSec + shift, endSec: c.endSec + shift }
          : c,
      );
      next.splice(index + 1, 0, clip);
      return {
        ...state,
        exports: invalidateMixExports(state.exports),
        musicClips: next,
      };
    }

    case actionTypes.UPDATE_MUSIC_CLIP: {
      const { id, changes } = action.payload;
      return {
        ...state,
        exports: invalidateMixExports(state.exports),
        musicClips: state.musicClips.map((c) =>
          c.id === id ? { ...c, ...changes } : c,
        ),
      };
    }

    case actionTypes.DELETE_MUSIC_CLIP: {
      return {
        ...state,
        exports: invalidateMixExports(state.exports),
        musicClips: state.musicClips.filter((c) => c.id !== action.payload),
      };
    }

    // A track level is mixed into the exported file, so a real change — not a
    // load-time restore (skipAutoSave) — makes the cached VIDEO / joined AUDIO
    // stale.
    case actionTypes.SET_TRACK_VOLUME: {
      const { track, value } = action.payload || {};
      if (!(track in state.volumes)) return state;
      const clamped = Math.max(0, Math.min(1, value));
      const volumes = { ...state.volumes, [track]: clamped };
      if (action.meta?.skipAutoSave) return { ...state, volumes };
      return {
        ...state,
        volumes,
        exports: invalidateMixExports(state.exports),
      };
    }

    // Load-time restore of every level: never invalidates the exports (nothing
    // changed, the project is only being reopened).
    case actionTypes.SET_TRACK_VOLUMES: {
      return {
        ...state,
        volumes: { ...state.volumes, ...(action.payload || {}) },
      };
    }

    case actionTypes.SET_VIDEO_SETTINGS:
      return {
        ...state,
        videoSettings: action.payload || createDefaultVideoSettings(),
      };

    case actionTypes.UPDATE_VIDEO_SETTINGS: {
      const changed = Object.entries(action.payload || {}).some(
        ([field, value]) => state.videoSettings?.[field] !== value,
      );
      return {
        ...state,
        isDraft: true,
        // A rendered mp4 bakes in aspect ratio / background color, so a real
        // change makes the cached VIDEO export stale (the Backend also drops
        // exports.VIDEO when videoSettings are saved). Audio exports are
        // unaffected — only the VIDEO slot is invalidated.
        exports:
          changed && state.exports
            ? omit(state.exports, [EXPORT_TYPE.VIDEO])
            : state.exports,
        videoSettings: {
          ...state.videoSettings,
          ...action.payload,
        },
      };
    }

    case actionTypes.APPLY_CAPTION_STYLE: {
      const { styleId, config } = action.payload;
      const current = state.videoSettings?.captionStyle;
      const isSameStyle = current?.styleId === styleId;
      if (isSameStyle && (current.config || !config)) return state;
      const videoSettings = withCustomCaptionStyle({
        ...state.videoSettings,
        // "Không hiển thị phụ đề" không có gì để cấu hình nên không kèm config.
        captionStyle: config
          ? { styleId, config: mergeCaptionConfig(config) }
          : { styleId },
      });
      if (isSameStyle) return { ...state, videoSettings };
      return {
        ...state,
        isDraft: true,
        // mp4 đã render bake sẵn phụ đề, giống aspectRatio.
        exports: state.exports
          ? omit(state.exports, [EXPORT_TYPE.VIDEO])
          : state.exports,
        videoSettings,
      };
    }

    case actionTypes.UPDATE_CAPTION_STYLE: {
      const { group, patch } = action.payload;
      const captionStyle = state.videoSettings?.captionStyle;
      const config = captionStyle?.config;
      if (!config) return state;
      // Editing a system style forks it: what is on screen stops being that
      // style and becomes the project's own.
      const styleId = isOwnCaptionStyle(captionStyle.styleId)
        ? captionStyle.styleId
        : CAPTION_STYLE_CUSTOM;
      return {
        ...state,
        isDraft: true,
        exports: state.exports
          ? omit(state.exports, [EXPORT_TYPE.VIDEO])
          : state.exports,
        videoSettings: withCustomCaptionStyle({
          ...state.videoSettings,
          captionStyle: {
            ...captionStyle,
            styleId,
            config: { ...config, [group]: { ...config[group], ...patch } },
          },
        }),
      };
    }

    case actionTypes.SET_ORIGINAL_VIDEO_SETTINGS:
      return {
        ...state,
        originalVideoSettings: action.payload || createDefaultVideoSettings(),
      };

    case actionTypes.ADD_BLOCK: {
      const { blockId, newBlocks, before } = action.payload;

      const blocksWithIds = newBlocks.map((block) => ({
        ...block,
        id: block.id || uuidv4(),
        audioLink: null,
      }));

      const newBlockIds = [...state.multiBlock.blockIds];
      const index = newBlockIds.indexOf(blockId);
      if (index === -1) {
        newBlockIds.push(...blocksWithIds.map((b) => b.id));
      } else {
        // before: ahead of the target; otherwise right after it.
        newBlockIds.splice(
          before ? index : index + 1,
          0,
          ...blocksWithIds.map((b) => b.id),
        );
      }

      return {
        ...state,
        isDraft: true,
        multiBlock: {
          ...state.multiBlock,
          blockIds: newBlockIds,
          blocks: {
            ...state.multiBlock.blocks,
            ...blocksWithIds.reduce((acc, b) => {
              acc[b.id] = b;
              return acc;
            }, {}),
          },
        },
      };
    }

    case actionTypes.UPDATE_BLOCK: {
      const { blockId, block } = action.payload;
      const shouldStopAudio =
        state.product === PROJECT_PRODUCT.DUBBING ||
        state.currentBlockId === blockId ||
        state.isMultiBlockMode;

      return {
        ...state,
        isDraft: true,
        isPlaying: shouldStopAudio ? false : state.isPlaying,
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            [blockId]: {
              ...state.multiBlock.blocks[blockId],
              ...block,
              // Remove the success icon. This is too prevent when the icon is shown, and the setTimeout
              // is running but user edit the block, which will make the useEffect run the clean up function
              // which will delete the setTimeout.
              showSuccessIcon: false,
              blockStatus: block.blockStatus || BLOCK_STATUS.DRAFT,
              isSample: false,
              isBlockEditting: true,
            },
          },
        },
      };
    }

    case actionTypes.UPDATE_EXTRACTION_BLOCKS: {
      const { blocks: incomingBlocks, syncOriginals = true } = action.payload;
      if (!Array.isArray(incomingBlocks) || !incomingBlocks.length) {
        return state;
      }

      const currentBlocks = state.multiBlock?.blocks || {};
      const currentOriginalBlocks = state.multiBlock?.originalBlocks || {};
      const nextBlocks = { ...currentBlocks };
      const nextOriginalBlocks = { ...currentOriginalBlocks };

      incomingBlocks.forEach((incoming) => {
        const blockId = incoming?.id;
        if (!blockId || !currentBlocks[blockId]) return;

        // Only merge fields streamed by the extraction polling.
        const note = incoming.originalNote;
        const extractedFields = {
          ...(incoming.text !== undefined ? { text: incoming.text } : {}),
          ...(note !== undefined
            ? { originalNote: note, currentNote: note }
            : {}),
          ...(incoming.previewImageUrl !== undefined
            ? { previewImageUrl: incoming.previewImageUrl }
            : {}),
        };

        nextBlocks[blockId] = { ...currentBlocks[blockId], ...extractedFields };
        // Keep originals in sync so filled-in data is not treated as a change.
        // Skipped for AI rewrite (syncOriginals=false): the new text MUST be
        // seen as a change so the synthesize button comes back.
        if (syncOriginals && currentOriginalBlocks[blockId]) {
          nextOriginalBlocks[blockId] = {
            ...currentOriginalBlocks[blockId],
            ...extractedFields,
          };
        }
      });

      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blocks: nextBlocks,
          originalBlocks: nextOriginalBlocks,
        },
      };
    }

    case actionTypes.SET_STREAMING_BLOCKS: {
      const { blocks: streamingBlocks, blockIds: streamingBlockIds } =
        action.payload;
      const maxCharLimit =
        state.product === PROJECT_PRODUCT.DUBBING
          ? MAX_CHARACTER_PER_BLOCK_SRT
          : MAX_CHARACTER_PER_BLOCK;
      // Add blockStatus to each streaming block (like SET_MULTI_BLOCK);
      // otherwise ContentBlock renders an incomplete block and throws.
      const blocksWithStatus = Object.values(streamingBlocks).reduce(
        (acc, block) => {
          const blockStatus = getBlockStatus({
            block,
            originalBlock: undefined,
            audioType: state.audioType,
            maxCharLimit,
            voice: resolveBlockVoice({
              block,
              speakers: state.speakers,
              voiceDefault: state.voiceDefault,
            }),
            isTranslationSuccess:
              state.translation?.status === TRANSLATION_STATUS.SUCCESS,
          });
          acc[block.id] = { ...block, blockStatus };
          return acc;
        },
        {},
      );
      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blocks: blocksWithStatus,
          blockIds: streamingBlockIds,
          // Keep originalBlocks/originalBlockIds unchanged so the auto-save
          // diff stays clean; SET_MULTI_BLOCK resets them when extraction ends.
        },
      };
    }

    case actionTypes.UPDATE_MULTIPLE_BLOCKS: {
      const { blockUpdates } = action.payload;
      const shouldStopAudio =
        state.product === PROJECT_PRODUCT.DUBBING ||
        blockUpdates.some(
          (update) =>
            state.currentBlockId === update.blockId || state.isMultiBlockMode,
        );

      const updatedBlocks = blockUpdates.reduce((acc, { blockId, block }) => {
        acc[blockId] = {
          ...state.multiBlock.blocks[blockId],
          ...block,
          showSuccessIcon: false,
          blockStatus: block.blockStatus || BLOCK_STATUS.DRAFT,
          isSample: false,
          isBlockEditting: true,
        };
        return acc;
      }, {});

      return {
        ...state,
        isDraft: true,
        isPlaying: shouldStopAudio ? false : state.isPlaying,
        currentBlockId: shouldStopAudio ? '' : state.currentBlockId,
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            ...updatedBlocks,
          },
        },
      };
    }

    case actionTypes.UPDATE_BLOCK_SYNTHESIS_INFO: {
      const { blockId, synthesisInfo } = action.payload;
      const { status } = synthesisInfo;
      const isSynthesisFailed = status === CONVERTING_STATUS.FAILURE;

      return {
        ...state,
        isDraft: isSynthesisFailed,
        exports: invalidateExports(state.exports),
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            [blockId]: {
              ...state.multiBlock.blocks[blockId],
              ...synthesisInfo,
              isBlockEditting: false,
            },
          },
        },
      };
    }

    case actionTypes.UPDATE_MULTI_BLOCK_SYNTHESIS_INFO: {
      const { blockIds, synthesisInfos } = action.payload;
      const isSynthesisFailed = synthesisInfos.some(
        (synthesisInfo) => synthesisInfo.status === CONVERTING_STATUS.FAILURE,
      );
      const isSynthesisInProgress = synthesisInfos.some(
        (synthesisInfo) =>
          synthesisInfo.status === CONVERTING_STATUS.IN_PROGRESS,
      );

      const newBlocks = blockIds.map((blockId) => ({
        ...state.multiBlock.blocks[blockId],
        ...synthesisInfos.find((synthesisInfo) => synthesisInfo.id === blockId),
        isBlockEditting: false,
      }));

      return {
        ...state,
        isDraft: isSynthesisFailed,
        isConverting: isSynthesisInProgress,
        exports: invalidateExports(state.exports),
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            ...newBlocks.reduce((acc, block) => {
              acc[block.id] = block;
              return acc;
            }, {}),
          },
        },
      };
    }

    case actionTypes.UPDATE_BLOCK_ELEMENTS: {
      const { blockId, elements } = action.payload;
      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            [blockId]: { ...state.multiBlock.blocks[blockId], elements },
          },
        },
      };
    }

    case actionTypes.UPDATE_ORDER_BLOCK: {
      const blockIds = action.payload;
      return {
        ...state,
        isDraft: true,
        multiBlock: {
          ...state.multiBlock,
          blockIds,
        },
      };
    }

    case actionTypes.DELETE_BLOCK: {
      const blockId = action.payload;
      return {
        ...state,
        isDraft: true,
        multiBlock: {
          ...state.multiBlock,
          blockIds: state.multiBlock.blockIds.filter((id) => id !== blockId),
          blocks: omit(state.multiBlock.blocks, [blockId]),
        },
      };
    }

    case actionTypes.DELETE_BLOCKS: {
      const { blockIds } = action.payload;
      return {
        ...state,
        isDraft: true,
        multiBlock: {
          ...state.multiBlock,
          blockIds: state.multiBlock.blockIds.filter(
            (id) => !blockIds.includes(id),
          ),
          blocks: omit(state.multiBlock.blocks, blockIds),
        },
        selectedBlockIds: state.selectedBlockIds.filter(
          (id) => !blockIds.includes(id),
        ),
      };
    }

    case actionTypes.UPDATE_DURATION: {
      return {
        ...state,
        duration: action.payload,
      };
    }

    case actionTypes.UPDATE_CURRENT_TIME: {
      return {
        ...state,
        currentTime: action.payload,
      };
    }

    case actionTypes.SET_IS_DOWNLOADING: {
      return {
        ...state,
        isDownloading: action.payload,
      };
    }

    case actionTypes.SET_EXPORTS: {
      return {
        ...state,
        exports: action.payload,
        // The store is not reset between projects, so without this the previous
        // project's history rows show up in this one.
        exportRecords: { items: [], nextCursor: null },
      };
    }

    case actionTypes.SET_EXPORT_BY_TYPE: {
      const { exportType, exportData } = action.payload;
      return {
        ...state,
        exports: {
          ...state.exports,
          [exportType]: exportData,
        },
      };
    }

    case actionTypes.SET_EXPORT_RECORDS_PAGE: {
      const { records, nextCursor, cursor } = action.payload;
      const loaded = cursor
        ? appendExportPage(state.exportRecords.items, records)
        : records;
      // A loaded page answers about a record as much as a progress tick does:
      // waiting for the next tick is the tab that still says loading.
      const { items, exports } = mergeProgressIntoState(
        { items: loaded, exports: state.exports },
        records,
      );
      return {
        ...state,
        exportRecords: { items, nextCursor: nextCursor || null },
        exports,
      };
    }

    case actionTypes.SET_EXPORT_DOWNLOADING: {
      const { id, isDownloading } = action.payload;
      const ids = state.downloadingExportIds;
      if (isDownloading)
        return ids.includes(id)
          ? state
          : { ...state, downloadingExportIds: [...ids, id] };
      return ids.includes(id)
        ? { ...state, downloadingExportIds: ids.filter((e) => e !== id) }
        : state;
    }

    case actionTypes.SET_EXPORT_PROGRESS: {
      const { items, exports } = mergeProgressIntoState(
        { items: state.exportRecords.items, exports: state.exports },
        action.payload.records,
      );
      return {
        ...state,
        exportRecords: { ...state.exportRecords, items },
        exports,
      };
    }

    case actionTypes.SET_IS_MULTI_BLOCK_MODE:
      return {
        ...state,
        isMultiBlockMode: action.payload,
      };

    case actionTypes.UPDATE_BLOCKS_WITH_PRONUNCIATION_CHANGE: {
      const { pronunciationChange } = action;
      const newBlocks = Object.values(state.multiBlock.blocks)
        .filter((block) =>
          block.text.toLowerCase().includes(pronunciationChange.toLowerCase()),
        )
        .map((block) => ({
          ...block,
          audioLink: null,
          status: null,
        }))
        .reduce((acc, block) => {
          acc[block.id] = block;
          return acc;
        }, {});

      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            ...newBlocks,
          },
        },
      };
    }

    case actionTypes.UPDATE_ORIGINAL_BLOCKS: {
      const blockId = action.payload;
      const { audioType } = state;
      let originalBlocks = { ...state.multiBlock.originalBlocks };

      if (blockId) {
        originalBlocks[blockId] = state.multiBlock.blocks[blockId];
      } else {
        originalBlocks = state?.multiBlock?.blocks;
      }
      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          originalBlocks,
          originalBlockIds: state?.multiBlock?.blockIds,
        },
        originalTitle: state.title,
        originalAudioType: audioType,
      };
    }

    case actionTypes.SET_IS_PROJECT_SYNTHESIZING: {
      return {
        ...state,
        isProjectSynthesizing: action.payload,
      };
    }
    case actionTypes.SET_IS_GENERATING_NARRATION: {
      const { isGeneratingNarration, blockIds: incomingBlockIds = [] } =
        action.payload;
      return {
        ...state,
        isGeneratingNarration,
        generatingBlockIds: isGeneratingNarration
          ? Array.from(
              new Set([...state.generatingBlockIds, ...incomingBlockIds]),
            )
          : [],
      };
    }
    case actionTypes.UPDATE_CREDITS_BLOCKS: {
      const { blockId, credits } = action.payload;
      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blockCredits: {
            ...state.multiBlock.blockCredits,
            [blockId]: credits,
          },
        },
      };
    }

    case actionTypes.DELETE_CREDITS_BLOCKS: {
      const { blockId } = action.payload;
      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blockCredits: omit(state.multiBlock.blockCredits, [blockId]),
        },
      };
    }

    case actionTypes.SET_ORIGINAL_TITLE: {
      return {
        ...state,
        originalTitle: action.payload,
      };
    }

    case actionTypes.SET_AUDIO_TYPE: {
      return {
        ...state,
        audioType: action.payload,
      };
    }

    case actionTypes.SET_OPEN_DIALOG_CHANGE_FORMAT: {
      return {
        ...state,
        openDialogChangeFormat: action.payload,
      };
    }

    case actionTypes.SAVE_PROJECT: {
      const {
        product,
        audioType,
        originalAudioType,
        videoSettings,
        originalVideoSettings,
      } = state;
      const { blocks, originalBlocks, blockIds } = state.multiBlock;
      const blockWithText = Object.keys(blocks)
        .filter((blockId) => blocks[blockId].text)
        .map((blockId) => blocks[blockId]);
      const blockChanged =
        product === PROJECT_PRODUCT.DUBBING
          ? getDubbingBlockChanged(blockWithText, originalBlocks)
          : getBlockChanged(blockWithText, originalBlocks);
      const newBlocks = blockChanged?.reduce((acc, block) => {
        acc[block.id] = {
          ...block,
          audioLink: null,
          voiceCode: block.voiceCode || state.voiceDefault.code,
        };
        return acc;
      }, {});

      // SAVE_PROJECT bakes the default voiceCode into changed blocks. On a freshly
      // created project the auto-save baseline was captured with voiceCode still
      // undefined, so without syncing it here the next edit's diff would see
      // voiceCode "changed" for every baked block and re-send it — which makes the
      // Backend drop the just-generated audio. Mirror the baked content into the
      // baseline so only genuine user edits are detected afterwards.
      const syncedLastSaved =
        state.autoSaveEnabled && newBlocks
          ? {
              ...state.lastSaved,
              blocks: {
                ...state.lastSaved.blocks,
                ...Object.fromEntries(
                  Object.keys(newBlocks).map((id) => [
                    id,
                    pickBlockContent(newBlocks[id], product),
                  ]),
                ),
              },
            }
          : null;

      const isProjectChanged =
        blockChanged.length || audioType !== originalAudioType;
      const hasVideoSettingsChanged =
        product === PROJECT_PRODUCT.DUBBING &&
        isVideoSettingsChanged(videoSettings, originalVideoSettings);

      return {
        ...state,
        isDraft: false,
        originalTitle: state.title,
        originalAudioType: state.audioType,
        originalVideoSettings: state.videoSettings,
        ...(syncedLastSaved ? { lastSaved: syncedLastSaved } : {}),
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...blocks,
            ...newBlocks,
          },
          originalBlocks: {
            ...blocks,
            ...newBlocks,
          },
          originalBlockIds: blockIds,
        },
        ...((isProjectChanged || hasVideoSettingsChanged) &&
        product === PROJECT_PRODUCT.DUBBING
          ? {
              audioLinkSrt: null,
              audioExpiredAt: null,
              requestId: null,
              status: null,
            }
          : {}),
      };
    }

    case actionTypes.SET_ORIGINAL_AUDIO_TYPE: {
      return {
        ...state,
        originalAudioType: action.payload,
      };
    }

    // This reducer is only concerned with the synthesis status of each block.
    // Although we have UPDATE_MULTI_BLOCK_SYNTHESIS_INFO, but that reducer update to much details
    // that is unnecessary.
    case actionTypes.UPDATE_MULTI_BLOCK_SYNTHESIS_STATUS: {
      const { blocks } = action.payload;

      const newBlocks = Object.keys(blocks).reduce((acc, blockId) => {
        acc[blockId] = {
          ...state.multiBlock.blocks[blockId],
          ...blocks[blockId],
        };
        return acc;
      }, {});

      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            ...newBlocks,
          },
        },
      };
    }

    case actionTypes.UPDATE_BLOCK_STATUS: {
      const { blockId, blockStatus } = action.payload;
      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            [blockId]: {
              ...state.multiBlock.blocks[blockId],
              blockStatus,
            },
          },
        },
      };
    }

    case actionTypes.UPDATE_BLOCKS_STATUS: {
      const { blockIds, blockStatus } = action.payload;
      const nextBlocks = { ...state.multiBlock.blocks };
      blockIds.forEach((blockId) => {
        if (!nextBlocks[blockId]) return;
        nextBlocks[blockId] = { ...nextBlocks[blockId], blockStatus };
      });
      return {
        ...state,
        multiBlock: { ...state.multiBlock, blocks: nextBlocks },
      };
    }

    case actionTypes.SET_MULTI_BLOCK_STATUS: {
      const { blocks } = action.payload;
      const maxCharLimit =
        state.product === PROJECT_PRODUCT.DUBBING
          ? MAX_CHARACTER_PER_BLOCK_SRT
          : MAX_CHARACTER_PER_BLOCK;
      const blocksWithStatus = Object.values(blocks).map((block) => {
        const blockStatus = getBlockStatus({
          block,
          originalBlock: state.multiBlock.originalBlocks[block.id],
          audioType: state.audioType,
          maxCharLimit,
          voice: resolveBlockVoice({
            block,
            speakers: state.speakers,
            voiceDefault: state.voiceDefault,
          }),
          isTranslationSuccess:
            state.translation?.status === TRANSLATION_STATUS.SUCCESS,
        });
        return {
          ...state.multiBlock.blocks[block.id],
          blockStatus,
        };
      });

      const newBlocks = blocksWithStatus.reduce((acc, block) => {
        acc[block.id] = block;
        return acc;
      }, {});

      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blocks: {
            ...state.multiBlock.blocks,
            ...newBlocks,
          },
        },
      };
    }

    case actionTypes.SET_FILTER_STATUS: {
      const { filterStatus } = action.payload;
      return {
        ...state,
        currentFilteringStatus: filterStatus,
      };
    }

    case actionTypes.SET_START_FILTER_BLOCK: {
      const { startFilterBlock } = action.payload;
      return {
        ...state,
        startFilterBlock,
      };
    }

    case actionTypes.SELECT_BLOCKS: {
      const { blockIds } = action.payload;
      return {
        ...state,
        selectedBlockIds: [...state.selectedBlockIds, ...blockIds],
      };
    }

    case actionTypes.UNSELECT_BLOCKS: {
      const { blockIds } = action.payload;
      return {
        ...state,
        selectedBlockIds: state.selectedBlockIds.filter(
          (id) => !blockIds.includes(id),
        ),
      };
    }

    case actionTypes.UNSELECT_ALL_BLOCKS: {
      return {
        ...state,
        selectedBlockIds: [],
      };
    }

    case actionTypes.SET_FILTER_VOICE_CODE: {
      const { filterVoiceCode } = action.payload;
      return {
        ...state,
        currentFilteringVoiceCode: filterVoiceCode,
      };
    }

    case actionTypes.SET_FILTER_SPEAKER_ID: {
      const { filterSpeakerId } = action.payload;
      return {
        ...state,
        currentFilteringSpeakerId: filterSpeakerId,
      };
    }

    case actionTypes.SET_SPEAKERS:
      return {
        ...state,
        speakers: action.payload || [],
      };

    case actionTypes.UPDATE_SPEAKER: {
      const { speakerId, speaker } = action.payload;
      return {
        ...state,
        speakers: state.speakers.map((s) =>
          s.id === speakerId ? { ...s, ...speaker } : s,
        ),
      };
    }

    case actionTypes.ADD_SPEAKER:
      return {
        ...state,
        speakers: [action.payload, ...state.speakers],
      };

    case actionTypes.DELETE_SPEAKER: {
      const speakerId = action.payload;
      return {
        ...state,
        speakers: state.speakers.filter((s) => s.id !== speakerId),
      };
    }

    case actionTypes.SET_AUDIO_LINK_SRT: {
      return {
        ...state,
        audioLinkSrt: action.payload,
      };
    }

    case actionTypes.SET_AUDIO_SRT_EXPIRED_AT: {
      return {
        ...state,
        audioSrtExpiredAt: action.payload,
      };
    }

    case actionTypes.SET_SOURCE: {
      return {
        ...state,
        source: action.payload,
      };
    }

    case actionTypes.SET_IS_BUFFERING: {
      return {
        ...state,
        isBuffering: action.payload,
      };
    }

    case actionTypes.SET_STATUS: {
      return {
        ...state,
        status: action.payload,
      };
    }

    case actionTypes.DELETE_ALL_BLOCKS_CREDITS: {
      return {
        ...state,
        multiBlock: {
          ...state.multiBlock,
          blockCredits: {},
        },
      };
    }

    case actionTypes.UPDATE_ALL_BLOCKS_CREDITS: {
      const { blockCredits } = action.payload;
      return {
        ...state,
        multiBlock: { ...state.multiBlock, blockCredits },
      };
    }
    case actionTypes.SET_DUBBING_REQUEST_ID: {
      return {
        ...state,
        dubbingRequestId: action.payload,
      };
    }

    case actionTypes.SET_IS_PROJECT_CONVERTING_DRAFT: {
      return {
        ...state,
        isProjectConvertingDraft: action.payload,
      };
    }

    case actionTypes.SET_IS_PRE_PROCESSING_SYNTHESIS: {
      return {
        ...state,
        isPreProcessingSynthesis: action.payload,
      };
    }

    case actionTypes.SET_SYNTHESIS_PARAMS:
      return { ...state, synthesisParams: action.payload };

    case actionTypes.SET_TRANSLATION: {
      const translation = action.payload;
      return {
        ...state,
        translation,
        // After translation completes successfully, show original text side-by-side by default.
        // Otherwise keep the editor focused on translated text.
        showOriginalText: translation?.status === TRANSLATION_STATUS.SUCCESS,
      };
    }

    case actionTypes.SET_IS_TRANSLATING: {
      return {
        ...state,
        isTranslating: action.payload,
      };
    }

    case actionTypes.SET_TRANSLATION_PENDING_SYNTHESIS: {
      return {
        ...state,
        isTranslationPendingSynthesis: action.payload,
      };
    }

    case actionTypes.TOGGLE_SHOW_ORIGINAL_TEXT: {
      return {
        ...state,
        showOriginalText: !state.showOriginalText,
      };
    }

    case actionTypes.CLEAR_TRANSLATION: {
      // Remove translatedText from all blocks. Deleting the translation reverts
      // each block to its original text, so any audio synthesized from the
      // translated text no longer matches — invalidate it too, mirroring the
      // Play → Convert reset done when the translation was first applied.
      const clearedBlocks = Object.keys(state.multiBlock.blocks).reduce(
        (acc, blockId) => {
          const { translatedText, ...blockWithoutTranslation } =
            state.multiBlock.blocks[blockId];
          acc[blockId] = invalidateBlockAudio(blockWithoutTranslation);
          return acc;
        },
        {},
      );

      // Under auto-save, the clear is already persisted by the deleteTranslation
      // API, so mirror it into the baseline (drop translatedText from every saved
      // block) — otherwise the next edit's diff would see translatedText "changed"
      // and try to re-send it.
      const syncedBaselineBlocks = state.autoSaveEnabled
        ? Object.fromEntries(
            Object.entries(state.lastSaved.blocks).map(([id, content]) => {
              const { translatedText, ...rest } = content;
              return [id, rest];
            }),
          )
        : null;

      return {
        ...state,
        translation: null,
        isTranslating: false,
        // Deleting translation invalidates current project audio context immediately.
        audioLinkSrt: null,
        audioExpiredAt: null,
        audioSrtExpiredAt: null,
        // Flip: if was pending (translated but not synthesized) → audio still matches original text → false
        // If was not pending (synthesized with translatedText) → audio doesn't match original text → true
        isTranslationPendingSynthesis: !state.isTranslationPendingSynthesis,
        showOriginalText: false,
        ...(syncedBaselineBlocks
          ? { lastSaved: { ...state.lastSaved, blocks: syncedBaselineBlocks } }
          : {}),
        multiBlock: {
          ...state.multiBlock,
          blocks: clearedBlocks,
        },
      };
    }

    case actionTypes.SET_IS_LOADING_PROJECT_DETAIL:
      return {
        ...state,
        isLoadingProjectDetail: action.payload,
      };

    case actionTypes.SET_AUTO_SAVE_STATUS:
      return { ...state, autoSaveStatus: action.payload };

    case actionTypes.SET_AUTO_SAVE_ENABLED:
      return { ...state, autoSaveEnabled: action.payload };

    case actionTypes.SET_AUTO_SAVE_VIDEO_CONFIG:
      return { ...state, autoSaveVideoConfig: action.payload };

    // Auto-save has just created the project's own style: point the panel at
    // the real id. Not a user edit - no history, and the baseline is patched in
    // the same pass, so it must not read as another change.
    case actionTypes.SET_PROJECT_CAPTION_TEMPLATE_ID: {
      const captionStyle = state.videoSettings?.captionStyle;
      if (!captionStyle?.config) return state;
      return {
        ...state,
        videoSettings: withCustomCaptionStyle({
          ...state.videoSettings,
          captionStyle: { ...captionStyle, styleId: action.payload },
        }),
      };
    }

    case actionTypes.PATCH_AUTO_SAVE_BASELINE: {
      const { blockIds, blocks, removedIds = [], meta = {} } = action.payload;
      const removedSet = new Set(removedIds);
      const mergedBlocks = { ...state.lastSaved.blocks, ...blocks };
      const nextBlocks = Object.fromEntries(
        Object.entries(mergedBlocks).filter(([id]) => !removedSet.has(id)),
      );
      return {
        ...state,
        lastSaved: {
          ...state.lastSaved,
          ...(blockIds ? { blockIds } : {}),
          blocks: nextBlocks,
          ...meta,
        },
      };
    }

    case historyActionTypes.HISTORY_APPLY_PATCHES: {
      const { scope, patches, options = {} } = action.payload;
      if (scope !== HISTORY_SCOPE.DUBBING) return state;

      // Apply Immer patches first, then overlay the per-entry snapshots
      // for block audio, originalBlocks, and project audio. Snapshots
      // cover state that doesn't flow through patches cleanly (synthesis
      // updates, SAVE_PROJECT writes to originalBlocks, etc.).
      const patched = applyForward(state, patches);
      // The project's own style has no delete endpoint, so undo cannot take its
      // card away — a reload would only bring it back.
      const ownStyle = state.videoSettings?.customCaptionStyle;
      const restored =
        ownStyle && !patched.videoSettings?.customCaptionStyle
          ? {
              ...patched,
              videoSettings: {
                ...patched.videoSettings,
                customCaptionStyle: ownStyle,
              },
            }
          : patched;
      // The block list derives what it shows from local state, and only
      // recomputes it while `startFilterBlock` is raised. An undo/redo can put
      // back blocks a filter had excluded, or move the filter itself, so raise
      // the flag on every restore — otherwise the list keeps the ids it held
      // before the patches landed and the restored blocks stay invisible.
      const withSnapshots = {
        ...applyHistorySnapshots(restored, options),
        startFilterBlock: true,
      };

      // `projectAudioSnapshot` already set the SRT audio; the legacy
      // checkpoint / invalidate paths only apply when the per-entry
      // snapshot is absent (e.g. older entries from before this rework).
      if (options.projectAudioSnapshot) return withSnapshots;
      if (options.audioState) {
        const { multiBlock: audioMultiBlock } = options.audioState;
        const audioState = omit(options.audioState, ['multiBlock', 'audioKey']);
        return {
          ...withSnapshots,
          ...audioState,
          multiBlock: {
            ...withSnapshots.multiBlock,
            ...(audioMultiBlock || {}),
          },
        };
      }
      if (options.invalidateAudio) {
        return {
          ...withSnapshots,
          audioLinkSrt: null,
          audioExpiredAt: null,
          audioSrtExpiredAt: null,
        };
      }
      return withSnapshots;
    }

    default:
      return state;
  }
}
